import { Api } from 'src/common';
import { PaymentStatus } from 'src/features/common/types/application/payment';

export enum RECEIPT_CHANNEL {
  EMAIL = 'email',
  SMS = 'sms',
}

interface IProduct {
  lineNumber: string;
  itemId: string;
  barcode: string;
  quantity: number;
  description: string;
  amountExVAT: number;
  amountVAT: number;
}

type PaymentRequest = {
  salesOrganization: string;
  agentId?: string;
  orderReferenceNumber: string;
  traceId: string;
  totalAmount: number; // cents
  basket: IProduct[];
  contactName?: string;
  msisdn?: string;
  receiptChannel?: RECEIPT_CHANNEL;
  email?: string;
  callbackUrl?: string; // self onboarding
};

interface IPaymentRequestResponse {
  transactionId: string;
  traceId: string;
  initiationUrl?: string;
  message: string;
  dependencyFailure: boolean;
}

const initiatePayment = async (
  paymentRequest: PaymentRequest,
  abortController: AbortController,
  handleTechnicalError,
): Promise<IPaymentRequestResponse> => {
  const ENDPOINT = 'initiate/payments';

  const { response, status } = await Api.post<IPaymentRequestResponse>(ENDPOINT, {
    body: JSON.stringify(paymentRequest),
    signal: abortController.signal,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (status === 424) {
    return {
      ...response,
      dependencyFailure: true,
    };
  }

  if (status >= 400) {
    handleTechnicalError && handleTechnicalError();
  }

  return response;
};

interface IPaymentStatusResponse {
  responseCode: string;
  responseMessage: string;
  transactionId: string;
}

type PaymentStatusType = { status: PaymentStatus } & IPaymentStatusResponse;

const getPaymentStatus = async (
  transactionId: string,
  abortController: AbortController,
): Promise<PaymentStatusType> => {
  const ENDPOINT = `payments/${transactionId}`;

  const { response, status } = await Api.get<IPaymentStatusResponse>(ENDPOINT, {
    signal: abortController.signal,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Conditions explained:
  // https://vodacom.atlassian.net/wiki/spaces/FS/pages/250249536/FSP-6446+SMME+Portal+-+Merchant+Assisted+onboarding+3rd+party+onboarding#6g.-Payment-Status-API

  if (status === 200 && response.responseCode === '200') {
    return {
      status: PaymentStatus.TIMEOUT,
      ...response,
    };
  }

  if (status === 504) {
    return {
      status: PaymentStatus.TIMEOUT,
      ...response,
    };
  }

  if (status === 202) {
    return {
      status: PaymentStatus.ORDER_IN_PROGRESS,
      ...response,
    };
  }

  if (status === 200 && response.responseCode === '00') {
    return {
      status: PaymentStatus.PAYMENT_SUCCESSFUL,
      ...response,
    };
  }

  return {
    status: PaymentStatus.PAYMENT_FAILED,
    ...response,
  };
};

export const cancelPayment = async (
  orderRefNumber: string,
  transactionId: string,
  handleTechnicalError,
) => {
  const ENDPOINT = `complete/payments`;

  const { response, status } = await Api.post(ENDPOINT, {
    body: JSON.stringify({
      orderReferenceNumber: orderRefNumber,
      transactionId: transactionId,
      action: 1, // Cancel action
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (status >= 400) {
    handleTechnicalError && handleTechnicalError();
  }

  return response;
};

export const RemotePaymentService = {
  initiatePayment,
  getPaymentStatus,
  cancelPayment,
};
